import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardBody, Container, Row, Col } from "reactstrap";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getTakeLensById } from "service/api";

const THEME = "#DC4C64";
const API_URL = process.env.REACT_APP_MYAPI_URL || "";

const COATING_LABELS = {
    uc: "UC",
    hc: "HC",
    hmc_blue: "HMC Blue",
    shmc_blue: "SHMC Blue",
    photo: "Photo",
};

const getLogoUrl = (logo) => {
    if (!logo) return null;
    const filename = logo.split("/").pop();
    return `${API_URL}/uploads/${filename}`;
};

const SectionTitle = ({ children }) => (
    <div style={{
        backgroundColor: THEME, color: "#fff", fontWeight: 700,
        fontSize: 13, padding: "5px 12px", borderRadius: "4px 4px 0 0", marginBottom: 0,
    }}>{children}</div>
);

const Field = ({ label, value }) => (
    <div style={{ marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 12, color: "#777" }}>{label}</span>
        <div style={{ fontSize: 13, color: "#222", fontWeight: 500, marginTop: 1 }}>
            {value || <span style={{ color: "#ccc" }}>—</span>}
        </div>
    </div>
);

const Badge = ({ value, green }) => {
    if (!value) return <span style={{ color: "#ccc" }}>—</span>;
    const bg = green ? "#e8f5e9" : "#fff0f3";
    const color = green ? "#2e7d32" : THEME;
    const border = green ? "1px solid #a5d6a7" : `1px solid ${THEME}`;
    return (
        <span style={{ background: bg, color, border, borderRadius: 4, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
            {value}
        </span>
    );
};

const EyeCard = ({ title, data }) => (
    <Card style={{ border: `1px solid ${THEME}`, borderRadius: 6, marginBottom: 0 }}>
        <div style={{ backgroundColor: "#fff0f3", borderBottom: `1px solid ${THEME}`, padding: "4px 12px", fontWeight: 700, fontSize: 12, color: THEME, borderRadius: "6px 6px 0 0" }}>
            {title}
        </div>
        <CardBody style={{ padding: "12px 14px" }}>
            <Field label="Lens Material" value={data.material} />
            <Field label="Vision" value={data.vision} />
            <Field label="Sub-Type" value={data.subvision} />
            <Field label="Coating" value={data.type ? <Badge value={COATING_LABELS[data.type] || data.type} /> : null} />
            <Field label="Photochromic" value={
                data.extra === "yes"
                    ? <Badge value="Yes" green />
                    : <span style={{ color: "#aaa", fontSize: 12 }}>No</span>
            } />
        </CardBody>
    </Card>
);

const ViewLenstaken = () => {
    const { id } = useParams();
    const [lens, setLens] = useState(null);
    const [loading, setLoading] = useState(true);
    const printRef = useRef(null);

    useEffect(() => {
        getTakeLensById(id)
            .then(res => setLens(res?.data))
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [id]);

    const handlePrint = () => {
        const content = printRef.current;
        const printWindow = window.open("", "_blank");
        const styleEl = printWindow.document.createElement("style");
        styleEl.textContent = `
            body { font-family: Arial, sans-serif; font-size: 13px; color: #222; margin: 20px; }
            .section-title { background: #DC4C64; color: #fff; font-weight: 700; padding: 5px 12px; border-radius: 4px 4px 0 0; font-size: 13px; }
            @media print { body { margin: 10px; } }
        `;
        const titleEl = printWindow.document.createElement("title");
        titleEl.textContent = "Lens Record";
        printWindow.document.head.appendChild(titleEl);
        printWindow.document.head.appendChild(styleEl);
        printWindow.document.body.innerHTML = content.innerHTML;
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
    };

    const handleDownloadPDF = async () => {
        const doc = new jsPDF();
        const pageW = doc.internal.pageSize.width;
        const margin = 10;
        const contentW = pageW - margin * 2;
        const themeRGB = [220, 76, 100];
        let startY = margin;

        const loadImg = async (url) => {
            if (!url) return null;
            try {
                const res = await fetch(url, { credentials: "omit" });
                if (!res.ok) return null;
                const blob = await res.blob();
                const dataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
                const dims = await new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => resolve({ w: img.width, h: img.height });
                    img.onerror = () => resolve({ w: 100, h: 100 });
                    img.src = dataUrl;
                });
                return { dataUrl, ...dims };
            } catch { return null; }
        };

        const p = lens.visit_id?.patientid;
        const c = lens.visit_id?.patientid?.clinicname || lens.clinic_id;
        const visitNo = lens.visit_no || lens.visit_id?.visit_no;

        const headStyles = { fillColor: themeRGB, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9, cellPadding: 3 };
        const lbStyle = { fontStyle: "bold", textColor: [120, 120, 120], fontSize: 7.5, fillColor: [248, 248, 248], cellPadding: 2.5 };
        const valStyle = { fontStyle: "normal", textColor: [25, 25, 25], fontSize: 9, fillColor: [255, 255, 255], cellPadding: 2.5 };
        const tableStyles = { lineColor: [225, 225, 225], lineWidth: 0.15 };
        const tableOpts = { margin: { left: margin, right: margin }, tableWidth: contentW, tableLineColor: [210, 210, 210], tableLineWidth: 0.2 };
        const lv = (label, value) => [
            { content: String(label), styles: lbStyle },
            { content: String(value || "—"), styles: valStyle },
        ];
        const colStyles4 = { 0: { cellWidth: 28 }, 1: { cellWidth: contentW / 2 - 28 }, 2: { cellWidth: 28 }, 3: { cellWidth: contentW / 2 - 28 } };

        // ── Header ──
        const logoUrl = getLogoUrl(c?.logo);
        const logoData = logoUrl ? await loadImg(logoUrl) : null;
        if (logoData) {
            const maxH = 14, maxW = 38;
            const ratio = Math.min(maxW / logoData.w, maxH / logoData.h);
            doc.addImage(logoData.dataUrl, "JPEG", margin, startY, logoData.w * ratio, logoData.h * ratio);
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.setTextColor(...themeRGB);
        doc.text("Lens Record Details", pageW - margin, startY + 6, { align: "right" });
        if (c?.name) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8.5);
            doc.setTextColor(110, 110, 110);
            doc.text(c.name, pageW - margin, startY + 12, { align: "right" });
        }
        startY += 19;
        doc.setDrawColor(...themeRGB);
        doc.setLineWidth(0.5);
        doc.line(margin, startY, pageW - margin, startY);
        startY += 4;

        // ── Patient ──
        const patientBody = p ? [
            [...lv("Title", p.title), ...lv("Date of Birth", p.dob)],
            [...lv("First Name", p.firstname), ...lv("Age", p.age?.toString())],
            [...lv("Last Name", p.lastname), ...lv("Gender", p.gender)],
            [...lv("MPI No.", p.mpino), ...lv("Contact", p.mobile)],
            [{ content: "ID Number", styles: lbStyle }, { content: String(p.idnumber || "—"), colSpan: 3, styles: valStyle }],
            [{ content: "Address", styles: lbStyle }, { content: String(p.address || "—"), colSpan: 3, styles: valStyle }],
        ] : [];
        if (visitNo) patientBody.push([{ content: "Visit No.", styles: lbStyle }, { content: String(visitNo), colSpan: 3, styles: valStyle }]);

        doc.autoTable({ startY, head: [[{ content: "Patient Details", colSpan: 4, styles: headStyles }]], body: patientBody, columnStyles: colStyles4, styles: tableStyles, ...tableOpts });
        startY = doc.lastAutoTable.finalY + 4;

        // ── Clinic ──
        const clinicBody = c ? [
            [...lv("Practice Name", c.name), ...lv("Address", c.address)],
            [...lv("Email", c.email), ...lv("City", c.city)],
            [...lv("Contact No.", c.contactno), ...lv("Province", c.province)],
        ] : [];
        doc.autoTable({ startY, head: [[{ content: "Practice / Clinic", colSpan: 4, styles: headStyles }]], body: clinicBody, columnStyles: colStyles4, styles: tableStyles, ...tableOpts });
        startY = doc.lastAutoTable.finalY + 4;

        // ── Lens Details ──
        const fLabel = 28, fVal = contentW / 2 - fLabel;
        const odVision = lens.lens_vision_od?.vision || lens.lens_vision?.vision;
        const osVision = lens.lens_vision_os?.vision || lens.lens_vision?.vision;
        const odSub = lens.lens_subvision_od?.sub_type || lens.lens_subvision?.sub_type;
        const osSub = lens.lens_subvision_os?.sub_type || lens.lens_subvision?.sub_type;
        const odCoating = COATING_LABELS[lens.lens_type_od || lens.lens_type] || lens.lens_type_od || lens.lens_type;
        const osCoating = COATING_LABELS[lens.lens_type_os || lens.lens_type] || lens.lens_type_os || lens.lens_type;

        doc.autoTable({
            startY,
            head: [[{ content: "Lens Details", colSpan: 4, styles: headStyles }]],
            body: [
                [{ content: "OD (Right Eye)", colSpan: 2, styles: { ...lbStyle, fillColor: [255, 240, 243], textColor: themeRGB, fontStyle: "bold" } },
                { content: "OS (Left Eye)", colSpan: 2, styles: { ...lbStyle, fillColor: [255, 240, 243], textColor: themeRGB, fontStyle: "bold" } }],
                [...lv("Material", lens.lens_material_od || lens.lens_material), ...lv("Material", lens.lens_material_os || lens.lens_material)],
                [...lv("Vision", odVision), ...lv("Vision", osVision)],
                [...lv("Sub-Type", odSub), ...lv("Sub-Type", osSub)],
                [...lv("Coating", odCoating), ...lv("Coating", osCoating)],
                [...lv("Photochromic", (lens.extra_od || lens.extra) === "yes" ? "Yes" : "No"),
                ...lv("Photochromic", (lens.extra_os || lens.extra) === "yes" ? "Yes" : "No")],
            ],
            columnStyles: { 0: { cellWidth: fLabel }, 1: { cellWidth: fVal }, 2: { cellWidth: fLabel }, 3: { cellWidth: fVal } },
            styles: tableStyles,
            ...tableOpts,
        });
        startY = doc.lastAutoTable.finalY + 4;

        doc.save(`lens-record-${id}.pdf`);
    };

    if (loading) return (
        <div className="page-content">
            <Container fluid>
                <div className="text-center py-5">
                    <span className="spinner-border" style={{ color: THEME }} />
                    <p className="mt-2 text-muted">Loading lens record...</p>
                </div>
            </Container>
        </div>
    );

    if (!lens) return (
        <div className="page-content">
            <Container fluid>
                <div className="text-center py-5 text-muted">Lens record not found.</div>
            </Container>
        </div>
    );

    const patient = lens.visit_id?.patientid;
    const clinic = patient?.clinicname || lens.clinic_id;
    const visit = lens.visit_id;

    const odData = {
        material: lens.lens_material_od || lens.lens_material,
        vision: lens.lens_vision_od?.vision || lens.lens_vision?.vision,
        subvision: lens.lens_subvision_od?.sub_type || lens.lens_subvision?.sub_type,
        type: lens.lens_type_od || lens.lens_type,
        extra: lens.extra_od || lens.extra,
    };
    const osData = {
        material: lens.lens_material_os || lens.lens_material,
        vision: lens.lens_vision_os?.vision || lens.lens_vision?.vision,
        subvision: lens.lens_subvision_os?.sub_type || lens.lens_subvision?.sub_type,
        type: lens.lens_type_os || lens.lens_type,
        extra: lens.extra_os || lens.extra,
    };

    return (
        <div className="page-content">
            <Container fluid>

                {/* Top bar */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 style={{ margin: 0, fontWeight: 700 }}>Lens Record Details</h4>
                    <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-success" onClick={handleDownloadPDF}>
                            <i className="mdi mdi-download me-1" /> Download PDF
                        </button>
                        <Link to="/list-takelens" className="btn btn-danger btn-sm">
                            <i className="mdi mdi-format-list-bulleted me-1" /> Back to List
                        </Link>
                    </div>
                </div>

                <div ref={printRef}>
                    <Row>

                        {/* ── Patient ── */}
                        <Col md={6} className="mb-3">
                            <SectionTitle><i className="mdi mdi-account me-1" /> Patient Details</SectionTitle>
                            <Card style={{ border: `1px solid ${THEME}`, borderTop: "none", borderRadius: "0 0 6px 6px" }}>
                                <CardBody>
                                    {patient ? (
                                        <Row>
                                            <Col md={6}>
                                                <Field label="Title" value={patient.title} />
                                                <Field label="First Name" value={patient.firstname} />
                                                <Field label="Last Name" value={patient.lastname} />
                                                <Field label="MPI No." value={patient.mpino} />
                                            </Col>
                                            <Col md={6}>
                                                <Field label="Date of Birth" value={patient.dob} />
                                                <Field label="Age" value={patient.age} />
                                                <Field label="Gender" value={patient.gender} />
                                                <Field label="Contact" value={patient.mobile} />
                                            </Col>
                                            <Col md={12}>
                                                <Field label="ID Number" value={patient.idnumber} />
                                                <Field label="Address" value={patient.address} />
                                            </Col>
                                        </Row>
                                    ) : (
                                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>No patient linked.</p>
                                    )}
                                    {(lens.visit_no || visit?.visit_no) && (
                                        <>
                                            <hr style={{ borderColor: "#f0f0f0", margin: "10px 0" }} />
                                            <Field label="Visit No." value={lens.visit_no || visit?.visit_no} />
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* ── Clinic ── */}
                        <Col md={6} className="mb-3">
                            <SectionTitle><i className="mdi mdi-hospital-building me-1" /> Practice / Clinic</SectionTitle>
                            <Card style={{ border: `1px solid ${THEME}`, borderTop: "none", borderRadius: "0 0 6px 6px" }}>
                                <CardBody>
                                    {clinic ? (
                                        <Row>
                                            <Col md={12}>
                                                {clinic.logo && (
                                                    <div className="mb-3">
                                                        <img
                                                            src={getLogoUrl(clinic.logo)}
                                                            alt="Clinic logo"
                                                            style={{ height: 60, objectFit: "contain", borderRadius: 4 }}
                                                        />
                                                    </div>
                                                )}
                                            </Col>
                                            <Col md={6}>
                                                <Field label="Practice Name" value={clinic.name} />
                                                <Field label="Email" value={clinic.email} />
                                                <Field label="Contact No." value={clinic.contactno} />
                                            </Col>
                                            <Col md={6}>
                                                <Field label="Address" value={clinic.address} />
                                                <Field label="City" value={clinic.city} />
                                                <Field label="Province" value={clinic.province} />
                                            </Col>
                                        </Row>
                                    ) : (
                                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>No practice information available.</p>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* ── Lens Details ── */}
                        <Col md={12} className="mb-3">
                            <SectionTitle><i className="mdi mdi-eye-outline me-1" /> Lens Details</SectionTitle>
                            <Card style={{ border: `1px solid ${THEME}`, borderTop: "none", borderRadius: "0 0 6px 6px" }}>
                                <CardBody>
                                    <Row>
                                        <Col md={6} className="mb-2">
                                            <EyeCard title="OD — Right Eye" data={odData} />
                                        </Col>
                                        <Col md={6} className="mb-2">
                                            <EyeCard title="OS — Left Eye" data={osData} />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>

                    </Row>
                </div>

            </Container>
        </div>
    );
};

export default ViewLenstaken;
